// =============================================================================
// DisplayBoardEditScreen — /display/edit — arrange the Live Board (mig 349).
//
// James, 4 Sep: "one that we can edit in the ops app by dragging widgets
// around in the display, adding widgets from an option list / removing ones
// … then we save / publish the live board screen … keeping the theme and
// style the same."
//
// Managers drag widgets to reorder, size them (narrow → full width), add
// from the palette, remove, then Save draft (private) or Publish (the TV
// picks it up within 5 minutes). Notices + Wire ticker items are managed
// on the same page. The board's look is fixed — layout is the only knob.
// =============================================================================

import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, ArrowRight, ExternalLink, GripVertical, Megaphone, MonitorPlay,
  Plus, Radio, Save, Trash2, UploadCloud, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Spinner, EmptyState } from "@/components/ui";
import { format } from "date-fns";

interface LayoutItem { id: string; widget: string; span: number; offset?: number }

const WIDGET_DEFS: Record<string, { label: string; blurb: string; defaultSpan: number }> = {
  jobs:     { label: "On site",             blurb: "Live jobs — today's build-plan line, crew, plant, plan progress.", defaultSpan: 4 },
  week:     { label: "The week",            blurb: "Who's where — crews across Mon–Sun.",                              defaultSpan: 5 },
  keydates: { label: "Key dates",           blurb: "★-flagged program dates coming up.",                               defaultSpan: 3 },
  calendar: { label: "Month calendar",      blurb: "The whole month, every crew, site-coloured.",                      defaultSpan: 8 },
  notices:  { label: "From the office",     blurb: "Bulletins you post below.",                                        defaultSpan: 4 },
  plant:    { label: "Plant bookings",      blurb: "Cranes, EWPs and gear booked over the next fortnight.",            defaultSpan: 4 },
  outages:  { label: "Outages",             blurb: "Night works and outage windows coming up.",                        defaultSpan: 4 },
  hse:      { label: "Safety & defects",    blurb: "Defects opened v closed this month, incidents, days since last.",  defaultSpan: 4 },
};

const SPAN_OPTIONS: { v: number; label: string }[] = [
  { v: 3, label: "Narrow" }, { v: 4, label: "Standard" }, { v: 5, label: "Medium" },
  { v: 6, label: "Half" }, { v: 8, label: "Wide" }, { v: 12, label: "Full width" },
];

export default function DisplayBoardEditScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();

  const { data: board, isLoading } = useQuery({
    queryKey: ["display_board_edit"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("display_board").select("*").limit(1).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const { data: notices = [] } = useQuery({
    queryKey: ["display_notices_edit"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("display_notices")
        .select("*").order("sort_order").order("created_at");
      if (error) throw error;
      return data ?? [];
    },
  });

  const [layout, setLayout] = useState<LayoutItem[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<null | "save" | "publish">(null);
  const [dragId, setDragId] = useState<string | null>(null);
  // HOOKS BEFORE EARLY RETURNS — these two originally sat below the loading
  // return, which crashed the whole app the moment the query resolved
  // (4 Sep: James's blank /display/edit page).
  const [noticeDraft, setNoticeDraft] = useState("");
  const [noticeKind, setNoticeKind] = useState<"notice" | "ticker">("notice");
  useEffect(() => {
    if (board && !dirty) setLayout((board.draft_layout ?? []) as LayoutItem[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board]);

  if (!user) return null;
  if (!hasRole(user, "manager")) {
    return <div className="p-6"><EmptyState icon={MonitorPlay} title="Managers only" description="The Live Board is arranged by managers." /></div>;
  }
  if (isLoading || !board) return <div className="p-6 flex justify-center"><Spinner /></div>;

  const change = (next: LayoutItem[]) => { setLayout(next); setDirty(true); };

  const addWidget = (key: string) => {
    change([...layout, { id: `w${Date.now()}`, widget: key, span: WIDGET_DEFS[key]?.defaultSpan ?? 4 }]);
  };
  const removeWidget = (id: string) => change(layout.filter(l => l.id !== id));
  const setSpan = (id: string, span: number) =>
    change(layout.map(l => l.id === id
      ? { ...l, span, offset: Math.min(l.offset ?? 0, Math.max(12 - span, 0)) }
      : l));
  // James, 4 Sep: "the ability to move widgets without them automatically
  // going to the furthest left position" — nudge adds/removes empty space
  // to the LEFT of a widget, so it can sit centre or right.
  const nudge = (id: string, dir: -1 | 1) =>
    change(layout.map(l => l.id === id
      ? { ...l, offset: Math.min(Math.max((l.offset ?? 0) + dir, 0), Math.max(12 - l.span, 0)) }
      : l));
  const dropOn = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const next = [...layout];
    const from = next.findIndex(l => l.id === dragId);
    const to = next.findIndex(l => l.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    change(next);
  };

  async function persist(publish: boolean) {
    setBusy(publish ? "publish" : "save");
    const upd: any = { draft_layout: layout, updated_at: new Date().toISOString() };
    if (publish) { upd.published_layout = layout; upd.published_at = new Date().toISOString(); }
    const { error } = await (supabase as any).from("display_board").update(upd).eq("board_id", board.board_id);
    setBusy(null);
    if (error) { alert(`Couldn't save: ${error.message}`); return; }
    setDirty(false);
    qc.invalidateQueries({ queryKey: ["display_board_edit"] });
    qc.invalidateQueries({ queryKey: ["display_board_bundle"] });
  }

  // ---- notices CRUD ----
  async function addNotice() {
    const body = noticeDraft.trim();
    if (!body) return;
    const { error } = await (supabase as any).from("display_notices")
      .insert({ kind: noticeKind, body, created_by: user!.user_id });
    if (error) { alert(error.message); return; }
    setNoticeDraft("");
    qc.invalidateQueries({ queryKey: ["display_notices_edit"] });
  }
  async function toggleNotice(n: any) {
    await (supabase as any).from("display_notices")
      .update({ is_active: !n.is_active, updated_at: new Date().toISOString() }).eq("notice_id", n.notice_id);
    qc.invalidateQueries({ queryKey: ["display_notices_edit"] });
  }
  async function deleteNotice(n: any) {
    if (!confirm("Delete this item for good?")) return;
    await (supabase as any).from("display_notices").delete().eq("notice_id", n.notice_id);
    qc.invalidateQueries({ queryKey: ["display_notices_edit"] });
  }

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-1 w-10 bg-accent rounded-full" />
            <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
              {board.name} · {board.published_at ? `published ${format(new Date(board.published_at), "d MMM HH:mm")}` : "never published"}
            </div>
          </div>
          <h1 className="text-[24px] font-semibold tracking-tight">Live Board</h1>
          <div className="text-[12px] text-text-muted mt-0.5">
            Drag to rearrange · size each widget · Publish sends it to the TV (it refreshes itself within 5 minutes).
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/display" target="_blank" rel="noreferrer"
             className="text-[12px] font-mono text-accent hover:underline inline-flex items-center gap-1.5">
            <ExternalLink size={13} /> Open the board
          </a>
          <Button variant="ghost" size="sm" icon={Save} disabled={!dirty || busy !== null} onClick={() => void persist(false)}>
            {busy === "save" ? "Saving…" : "Save draft"}
          </Button>
          <Button size="sm" icon={UploadCloud} disabled={busy !== null} onClick={() => void persist(true)}>
            {busy === "publish" ? "Publishing…" : "Publish to TV"}
          </Button>
        </div>
      </div>

      {/* add — ALWAYS visible, preset list (James, 4 Sep: "an Add function
          that has a drop down of pre set widget options") */}
      <div className="mb-4 flex items-center gap-3">
        <div className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/25 rounded-lg px-2 py-1">
          <Plus size={13} className="text-accent" />
          <select
            value=""
            onChange={(e) => { if (e.target.value) addWidget(e.target.value); }}
            className="bg-transparent text-[12.5px] font-semibold text-accent py-1 pr-1 cursor-pointer outline-none">
            <option value="">Add a widget…</option>
            {Object.entries(WIDGET_DEFS).map(([k, d]) => (
              <option key={k} value={k} disabled={layout.some(l => l.widget === k)}>
                {d.label}{layout.some(l => l.widget === k) ? "  ✓ on the board" : ""}
              </option>
            ))}
          </select>
        </div>
        <span className="text-[11px] text-text-muted">
          Anything longer than its box floats through, news-room style.
        </span>
      </div>

      {/* arrangement — a to-scale 12-column mini board */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-12 gap-3">
            {layout.map(item => {
              const def = WIDGET_DEFS[item.widget];
              const spanC = Math.min(Math.max(item.span, 3), 12);
              const offset = Math.min(Math.max(item.offset ?? 0, 0), 12 - spanC);
              return (
                <React.Fragment key={item.id}>
                {offset > 0 && (
                  <div style={{ gridColumn: `span ${offset}` }}
                       className="rounded-xl border border-dashed border-line/40 min-h-[52px]"
                       aria-hidden />
                )}
                <div
                     draggable
                     onDragStart={() => setDragId(item.id)}
                     onDragEnd={() => setDragId(null)}
                     onDragOver={(e) => e.preventDefault()}
                     onDrop={() => dropOn(item.id)}
                     style={{ gridColumn: `span ${spanC}` }}
                     className={`rounded-xl border-2 border-dashed p-3 select-none cursor-grab active:cursor-grabbing transition-colors ${
                       dragId === item.id ? "border-accent bg-accent/10 opacity-70" : "border-line bg-ink-raised/40 hover:border-accent/50"}`}>
                  <div className="flex items-center gap-2">
                    <GripVertical size={14} className="text-text-dim shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-text truncate">{def?.label ?? item.widget}</div>
                      <div className="text-[10.5px] text-text-muted truncate">{def?.blurb ?? ""}</div>
                    </div>
                    <select
                      value={item.span}
                      onChange={(e) => setSpan(item.id, Number(e.target.value))}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-ink-card border border-line rounded-md text-[11px] px-1.5 py-1 text-text">
                      {SPAN_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                    <span className="inline-flex items-center border border-line rounded-md overflow-hidden">
                      <button type="button" onClick={() => nudge(item.id, -1)}
                        className="p-1 text-text-dim hover:text-accent disabled:opacity-30"
                        disabled={(item.offset ?? 0) === 0}
                        title="Nudge left (remove empty space before it)">
                        <ArrowLeft size={12} />
                      </button>
                      <button type="button" onClick={() => nudge(item.id, 1)}
                        className="p-1 text-text-dim hover:text-accent disabled:opacity-30"
                        disabled={(item.offset ?? 0) >= 12 - item.span}
                        title="Nudge right (add empty space before it — sit it centre or right)">
                        <ArrowRight size={12} />
                      </button>
                    </span>
                    <button type="button" onClick={() => removeWidget(item.id)}
                      className="text-text-dim hover:text-danger p-1" title="Remove from the board">
                      <X size={14} />
                    </button>
                  </div>
                </div>
                </React.Fragment>
              );
            })}
          </div>
          {layout.length === 0 && (
            <div className="text-[13px] text-text-muted py-8 text-center">
              Empty board — add widgets from the row above.
            </div>
          )}
        </div>
      </Card>

      {/* notices + ticker */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(["notice", "ticker"] as const).map(kind => (
          <Card key={kind}>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-3 inline-flex items-center gap-1.5">
                {kind === "notice" ? <Megaphone size={11} className="text-accent" /> : <Radio size={11} className="text-accent" />}
                {kind === "notice" ? "Notices — the From the office widget" : "Coretel Wire — ticker items"}
              </div>
              <div className="space-y-1.5 mb-3">
                {notices.filter((n: any) => n.kind === kind).map((n: any) => (
                  <div key={n.notice_id}
                       className={`flex items-start gap-2.5 border border-line rounded-lg px-3 py-2 ${n.is_active ? "" : "opacity-45"}`}>
                    <div className="text-[12.5px] text-text flex-1 min-w-0">{n.body}</div>
                    <button type="button" onClick={() => void toggleNotice(n)}
                      className={`text-[10.5px] font-semibold px-2 py-[2px] rounded-full border shrink-0 ${
                        n.is_active ? "border-emerald-500/40 text-emerald-500" : "border-line text-text-muted"}`}>
                      {n.is_active ? "showing" : "off"}
                    </button>
                    <button type="button" onClick={() => void deleteNotice(n)}
                      className="text-text-dim hover:text-danger shrink-0 p-0.5"><Trash2 size={12} /></button>
                  </div>
                ))}
                {notices.filter((n: any) => n.kind === kind).length === 0 && (
                  <div className="text-[12px] text-text-muted py-2">Nothing yet.</div>
                )}
              </div>
              {noticeKind === kind ? (
                <div className="flex gap-2">
                  <input
                    value={noticeDraft}
                    onChange={(e) => setNoticeDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") void addNotice(); }}
                    placeholder={kind === "notice" ? "BBQ Friday 12:00 at the depot…" : "Beechmont crane arrives 06:30 Wed — carpark closed…"}
                    className="flex-1 bg-ink-card border border-line rounded-md px-3 py-2 text-[13px] text-text placeholder:text-text-dim"
                  />
                  <Button size="sm" icon={Plus} onClick={() => void addNotice()}>Add</Button>
                </div>
              ) : (
                <button type="button" onClick={() => { setNoticeKind(kind); setNoticeDraft(""); }}
                  className="text-[12px] font-mono text-accent hover:underline">+ add {kind === "notice" ? "a notice" : "a ticker item"}</button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
